/**
 * JS Plugin to lazy load assets (images, iframes, scripts and css files)
 * depends on activateOnScroll.js, lazyIframe.js
 * created by bogdan.b@codingheads.com
 * requires IntersectionObserver, MutationObserver, activateOnScroll.js, lazyIframe.js
 *
 * version 0.2
 */
class lazyLoadAssets {
  container = document.body;
  useMutationObserver = true;

  constructor(
    container = document.body,
    { useMutationObserver = true, init = false } = {}
  ) {
    // save the settings
    this.container = container;
    this.useMutationObserver = useMutationObserver;
    if (init) this.init();
  }

  activate() {
    // lazy images
    const lazyImages = this.container.querySelectorAll(
      ['interchange', 'bg', 'img']
        .reduce(
          (selector, type) => [
            ...selector,
            `[data-lazy-${type}]:not([data-lazyimg-init])`,
          ],
          []
        )
        .join(', ')
    );
    if (lazyImages.length)
      lazyImages.forEach(element => {
        new activateOnScroll(element, { initAttribute: 'lazyimgInit' });
      });

    // lazy iframes
    const lazyIframeElements = this.container.querySelectorAll(
      `[data-lazy-iframe]:not([data-lazyiframe-init])`
    );
    if (lazyIframeElements.length) {
      lazyIframeElements.forEach(element => new lazyIframe(element));
    }

    // lazy CSS for bg images
    const lazyCssBg = this.container.querySelectorAll(
      `[data-lazy-css-bg]:not([data-lazycssbg-init])`
    );
    if (lazyCssBg.length) {
      lazyCssBg.forEach(element => {
        new activateOnScroll(element, {
          initAttribute: 'lazycssbgInit',
          callback: target => {
            setTimeout(() => {
              imagesLoaded(target, { background: true }, function() {
                requestAnimationFrame(() => {
                  target.dataset.cssBgLoaded = true;
                  target.dispatchEvent(
                    new CustomEvent('lazyCssBgLoaded', { bubbles: true })
                  );
                });
              });
            });
          },
        });
      });
    }

    // lazy-load scripts
    const lazyScripts = this.container.querySelectorAll(
      `[data-lazy-script]:not([data-lazyscript-init])`
    );
    if (lazyScripts.length) {
      lazyScripts.forEach(element => {
        new activateOnScroll(element, {
          initAttribute: 'lazyscriptInit',
          options: {
            rootMargin: '600px 200px',
          },
          callback: target => {
            let scripts = target.dataset.lazyScript;
            try {
              const scriptsArray = JSON.parse(scripts);
              scripts = scriptsArray;
            } catch (e) {
              scripts = [scripts];
            }
            scripts.forEach(script => {
              const scriptElement = document.createElement('script');
              scriptElement.src = script;
              document.head.appendChild(scriptElement);
            });
          },
        });
      });
    }

    // lazy-load scripts
    const lazyStyles = this.container.querySelectorAll(
      `[data-lazy-stylesheet]:not([data-lazystylesheet-init])`
    );
    if (lazyStyles.length) {
      lazyStyles.forEach(element => {
        new activateOnScroll(element, {
          initAttribute: 'lazystylesheetInit',
          options: {
            rootMargin: '600px 200px',
          },
          callback: target => {
            let styles = target.dataset.lazyStylesheet;
            try {
              const scriptsArray = JSON.parse(styles);
              styles = scriptsArray;
            } catch (e) {
              styles = [styles];
            }
            styles.forEach(style => {
              const styleElement = document.createElement('link');
              styleElement.rel = 'stylesheet';
              styleElement.type = 'text/css';
              styleElement.href = style;
              document.head.appendChild(styleElement);
            });
          },
        });
      });
    }
  }

  throttledActivate = activateOnScroll.throttle(() => this.activate(), 100);

  init() {
    // activate the lazy assets
    this.activate();

    // observe if new elements are added - for example, by AJAX or infinite scroll
    if (this.useMutationObserver && 'MutationObserver' in window) {
      this.observer = new MutationObserver(mutationList => {
        // check if new elements are added
        const addedNodes = mutationList.reduce(
          (added, mutation) => [
            ...added,
            ...[].filter.call(
              mutation.addedNodes,
              node =>
                node.nodeType == Node.ELEMENT_NODE &&
                !['SCRIPT', 'LINK'].includes(node.tagName)
            ),
          ],
          []
        );
        if (addedNodes.length) {
          // clear the initialization attributes on the new nodes, to allow lazy-load to work
          addedNodes.forEach(node => {
            node.removeAttribute('data-lazyimg-init');
            node.removeAttribute('data-lazyiframe-init');
            node.removeAttribute('data-lazycssbg-init');
            const toClear = node.querySelectorAll(
              [
                '[data-lazyimg-init]',
                '[data-lazyiframe-init]',
                '[data-lazycssbg-init]',
              ].join(', ')
            );
            if (toClear.length)
              toClear.forEach(item => {
                if (!item.activateOnScrollInstance) {
                  item.removeAttribute('data-lazyimg-init');
                  item.removeAttribute('data-lazyiframe-init');
                  item.removeAttribute('data-lazycssbg-init');
                  if (item.style.opacity == 0) item.style.opacity = '';
                }
              });
          });
          this.throttledActivate();
        }
      });
      this.observer.observe(this.container, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }
  }
}
window.lazyLoadAssets = lazyLoadAssets;

jQuery(function($) {
  // lazy-load iframes
  $.fn.lazyLoadAssets = function(useMutationObserver = true) {
    this.each(function() {
      new lazyLoadAssets(this, { init: true });
    });
    return this;
  };

  $(document).lazyLoadAssets();
});
